// src/pages/BuyGymExercise.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import trainingAPI from '../services/trainingApi';
import {
    Box, Typography, Card, CardContent, Button, Alert, CircularProgress,
    Chip, CardMedia, IconButton, useTheme, Drawer, useMediaQuery, Fab,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid
} from '@mui/material';
import {
    SportsMartialArts, Menu as MenuIcon, Close, ShoppingCart,
    Person, CalendarMonth, AccessTime, FitnessCenter, EmojiEvents,
    Restaurant, Article
} from '@mui/icons-material';
import UserNavbar from '../components/UserNavbar';
import SideMenu from '../components/SideMenu';

const BuyGymExercise = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useCustomTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const [trainingTypes, setTrainingTypes] = useState([]);
    const [myBookings, setMyBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [bookingDialog, setBookingDialog] = useState({ open: false, training: null });

    useEffect(() => { fetchTrainings(); }, []);

    const fetchTrainings = async () => {
        try {
            setLoading(true);
            setError('');
            const trainingsData = await trainingAPI.getAll();
            const mappedTrainings = trainingsData.map((training, index) => ({
                id: training.id,
                name: training.name,
                description: training.description,
                duration: `${training.durationDays} денови`,
                price: training.price,
                type: index % 3 === 0 ? 'Персонален' : index % 3 === 1 ? 'Групен' : 'Мал групен',
                level: index % 3 === 0 ? 'Почетник' : index % 3 === 1 ? 'Среден' : 'Напреден',
                icon: <SportsMartialArts sx={{ fontSize: 60 }} />,
                color: getColorForIndex(index),
                features: getDefaultFeatures(training.name)
            }));
            setTrainingTypes(mappedTrainings);
            try {
                const myTrainingsData = await trainingAPI.getMyTrainings();
                setMyBookings(myTrainingsData);
            } catch (err) {
                console.log('ℹ️ No purchased trainings yet');
            }
        } catch (err) {
            console.error('❌ Error fetching trainings:', err);
            setError('Грешка при вчитување на тренинзите. Обидете се повторно.');
        } finally {
            setLoading(false);
        }
    };

    const getColorForIndex = (index) => {
        const colors = ['#AB47BC', '#FF6B6B', '#4ECDC4', '#FFD93D', '#F38BA3', '#6BCB77'];
        return colors[index % colors.length];
    };

    const getDefaultFeatures = (name) => [
        'Професионален тренер',
        'Флексибилен распоред',
        'Модерна опрема',
        'Персонализиран пристап'
    ];

    // ✅ IST menuItems KAKO UserDashboard
    const menuItems = [
        {
            id: 1,
            title: 'Купи Програми',
            description: 'Прегледај и купи тренинг програми креирани од експерти за максимални резултати',
            icon: <FitnessCenter sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/buy-programs',
            color: '#ff7eb9',
            available: true,
            badge: 'Популарно'
        },
        {
            id: 2,
            title: 'Купи Диета',
            description: 'Здравствени диететски планови прилагодени за вашите фитнес цели',
            icon: <Restaurant sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-diets',
            color: '#4CAF50',
            available: true,
            badge: 'Препорачано'
        },
        {
            id: 3,
            title: 'Блог',
            description: 'Читај статии, совети и информации за фитнес и здравје',
            icon: <Article sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-blog',
            color: '#a5d8ff',
            available: true,
            badge: 'Ново'
        },
        {
            id: 4,
            title: 'Достигнувања',
            description: 'Следете ги вашите достигнувања, прогресија и фитнес милници',
            icon: <EmojiEvents sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/achievements',
            color: '#ffd166',
            available: false,
            badge: 'Наскоро'
        }
    ];

    const handleItemClick = (item) => {
        setSelectedItem(item);
        if (isMobile) setMobileMenuOpen(false);
        if (!item.available) {
            alert('🚧 Оваа функционалност е во развој и наскоро ќе биде достапна!');
            return;
        }
        navigate(item.path);
    };

    const handleItemHover = (item) => {
        if (!isMobile) setSelectedItem(item);
    };

    const handleBookingClick = (training) => {
        setBookingDialog({ open: true, training });
    };

    const handleBookingConfirm = async () => {
        const training = bookingDialog.training;
        if (!training) return;
        try {
            setLoading(true);
            const result = await trainingAPI.purchase(training.id);
            alert(`🎉 Успешно го купивте тренингот "${training.name}"!\n\nЌе добиете потврда на email.`);
            setBookingDialog({ open: false, training: null });
            fetchTrainings();
        } catch (err) {
            console.error('❌ Purchase error:', err);
            alert('Грешка при купување. Обидете се повторно.');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price) => `${price.toLocaleString()} ден`;

    const userName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : 'Корисник';

    const TrainingCard = ({ training }) => (
        <Card sx={{
            width: isMobile ? 280 : isTablet ? 260 : 320,
            minWidth: isMobile ? 280 : isTablet ? 260 : 320,
            backgroundColor: 'background.card',
            border: `2px solid ${training.color}`,
            borderRadius: 3, transition: 'all 0.3s ease', overflow: 'hidden', cursor: 'pointer',
            '&:hover': { transform: isMobile ? 'scale(1.02)' : 'scale(1.05)', boxShadow: `0 12px 35px ${training.color}50`, zIndex: 10, '& .action-button': { opacity: 1, transform: 'translateY(0)' } }
        }}>
            <CardMedia component="div" sx={{
                height: isMobile ? 140 : 180,
                background: `linear-gradient(135deg, ${training.color} 0%, ${isDarkMode ? '#222' : '#f5f5f5'} 100%)`,
                position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: `3px solid ${training.color}`
            }}>
                <Box sx={{ color: 'white', opacity: 0.9 }}>{training.icon}</Box>
                <Chip label={training.type} size="small" sx={{ position: 'absolute', top: 12, right: 12, backgroundColor: training.color, color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }} />
                <Chip label={training.level} size="small" sx={{ position: 'absolute', top: 12, left: 12, backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }} />
            </CardMedia>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1.5 }}>{training.name}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, minHeight: '40px' }}>{training.description}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip icon={<AccessTime sx={{ fontSize: 14 }} />} label={training.duration} size="small" sx={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', fontSize: '0.7rem', fontWeight: 'bold' }} />
                </Box>
                <Box sx={{ mb: 2 }}>
                    {training.features.slice(0, 3).map((feature, index) => (
                        <Typography key={index} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.75rem' }}>✓ {feature}</Typography>
                    ))}
                </Box>
                <Box sx={{ textAlign: 'center', mb: 2, p: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ color: training.color, fontWeight: 'bold' }}>{formatPrice(training.price)}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>месечно</Typography>
                </Box>
                <Button fullWidth variant="contained" startIcon={<ShoppingCart />} onClick={() => handleBookingClick(training)} className="action-button"
                        sx={{ backgroundColor: training.color, color: 'white', fontWeight: 'bold', py: 1.2, opacity: 0.9, transform: 'translateY(5px)', transition: 'all 0.3s ease', '&:hover': { backgroundColor: training.color, opacity: 1, transform: 'translateY(-2px)', boxShadow: `0 6px 20px ${training.color}60` } }}>
                    Купи сега
                </Button>
            </CardContent>
        </Card>
    );

    const Section = ({ title, subtitle, children }) => (
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Box sx={{ mb: 3, px: { xs: 2, md: 0 } }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>{title}</Typography>
                {subtitle && <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: '500' }}>{subtitle}</Typography>}
            </Box>
            {children}
        </Box>
    );

    if (loading) {
        return (
            <>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', backgroundColor: 'background.default' }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
            </>
        );
    }

    return (
        <>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>
            <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>

                {/* DESKTOP SIDEBAR */}
                {!isMobile && (
                    <Box sx={{ width: isTablet ? '280px' : '320px', backgroundColor: 'background.paper', borderRight: 1, borderColor: 'divider', flexShrink: 0, position: 'sticky', top: '64px', height: 'calc(100vh - 64px)', overflowY: 'auto', zIndex: 999 }}>
                        <SideMenu menuItems={menuItems} selectedItem={selectedItem} onItemClick={handleItemClick} onItemHover={handleItemHover} programsCount={trainingTypes.length} userName={userName} />
                    </Box>
                )}

                {/* MOBILE DRAWER */}
                {isMobile && (
                    <>
                        <Drawer anchor="left" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}
                                PaperProps={{ sx: { width: '280px', backgroundColor: 'background.paper', borderRight: 1, borderColor: 'divider' } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                <IconButton onClick={() => setMobileMenuOpen(false)}><Close /></IconButton>
                            </Box>
                            <SideMenu menuItems={menuItems} selectedItem={selectedItem} onItemClick={handleItemClick} onItemHover={handleItemHover} programsCount={trainingTypes.length} userName={userName} />
                        </Drawer>
                        <Fab color="primary" onClick={() => setMobileMenuOpen(true)} sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1000 }}><MenuIcon /></Fab>
                    </>
                )}

                {/* MAIN CONTENT */}
                <Box sx={{ flex: 1, p: { xs: 1, sm: 2, md: 4 }, backgroundColor: 'background.default', overflow: 'auto' }}>
                    <Box sx={{ mb: { xs: 4, md: 6 }, px: { xs: 2, md: 0 }, textAlign: { xs: 'center', md: 'left' } }}>
                        <Typography variant={isMobile ? 'h4' : 'h2'} sx={{ fontWeight: 'bold', mb: 2, background: 'linear-gradient(45deg, #AB47BC, #9C27B0)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            💪 Тренинг во Живо
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Закажете персонален или групен тренинг</Typography>
                    </Box>

                    {error && <Box sx={{ px: { xs: 2, md: 0 }, mb: 4 }}><Alert severity="error">{error}</Alert></Box>}

                    <Section title="Достапни Тренинзи" subtitle={`${trainingTypes.length} типови на професионални тренинзи`}>
                        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, px: { xs: 2, md: 0 }, '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-track': { backgroundColor: isDarkMode ? '#333' : '#e0e0e0', borderRadius: 4 }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#AB47BC', borderRadius: 4 } }}>
                            {trainingTypes.map(training => <TrainingCard key={training.id} training={training} />)}
                        </Box>
                    </Section>

                    <Section title="Мои Тренинзи" subtitle="Вашите купени тренинзи">
                        <Box sx={{ px: { xs: 2, md: 0 } }}>
                            {myBookings.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
                                    {myBookings.map((booking) => (
                                        <Card key={booking.id} sx={{ minWidth: 300, backgroundColor: 'background.card', border: 2, borderColor: 'primary.main' }}>
                                            <CardContent>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>{booking.trainInGym.name}</Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{booking.trainInGym.description}</Typography>
                                                <Chip label={`Преостануваат ${booking.remainingDays} денови`} color={booking.isActive ? 'success' : 'default'} sx={{ mb: 1 }} />
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                    Датум: {new Date(booking.purchaseDate).toLocaleDateString('mk-MK')}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            ) : (
                                <Card sx={{ maxWidth: 800, mx: 'auto', backgroundColor: 'background.paper', border: 2, borderColor: '#AB47BC', borderStyle: 'dashed', borderRadius: 3 }}>
                                    <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                                        <CalendarMonth sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                                        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 2 }}>Немате купено тренинзи</Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>Купете од достапните тренинзи погоре за да започнете</Typography>
                                        <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto' }}>💪 Откако ќе купите тренинг, тој ќе се појави овде</Alert>
                                    </CardContent>
                                </Card>
                            )}
                        </Box>
                    </Section>

                    <Box sx={{ px: { xs: 2, md: 0 }, mt: 6 }}>
                        <Card sx={{ maxWidth: 900, mx: 'auto', background: 'linear-gradient(135deg, rgba(171,71,188,0.1) 0%, rgba(156,39,176,0.1) 100%)', border: 1, borderColor: 'divider' }}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Typography variant="h6" sx={{ color: '#AB47BC', fontWeight: 'bold', mb: 2, textAlign: 'center' }}>ℹ️ Информации за тренинзи</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <CalendarMonth sx={{ fontSize: 40, color: '#AB47BC', mb: 1 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Флексибилно закажување</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Изберете време кое ви одговара</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Person sx={{ fontSize: 40, color: '#AB47BC', mb: 1 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Професионални тренери</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Сертифицирани и искусни</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <ShoppingCart sx={{ fontSize: 40, color: '#AB47BC', mb: 1 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Лесна резервација</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Брза и едноставна постапка</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={{ height: { xs: '100px', md: '200px' } }} />
                </Box>
            </Box>

            {/* Booking Dialog */}
            <Dialog open={bookingDialog.open} onClose={() => setBookingDialog({ open: false, training: null })} maxWidth="sm" fullWidth
                    PaperProps={{ sx: { backgroundColor: 'background.paper', border: 1, borderColor: 'divider' } }}>
                <DialogTitle sx={{ textAlign: 'center', color: '#AB47BC', fontWeight: 'bold' }}>🛒 Купи Тренинг</DialogTitle>
                <DialogContent>
                    {bookingDialog.training && (
                        <Box sx={{ py: 2 }}>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Box sx={{ color: bookingDialog.training.color, mb: 2 }}>{bookingDialog.training.icon}</Box>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>{bookingDialog.training.name}</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{bookingDialog.training.description}</Typography>
                                <Box sx={{ bgcolor: 'rgba(171,71,188,0.1)', p: 2, borderRadius: 2, border: 2, borderColor: bookingDialog.training.color, mb: 3 }}>
                                    <Typography variant="h3" sx={{ color: bookingDialog.training.color, fontWeight: 'bold' }}>{formatPrice(bookingDialog.training.price)}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{bookingDialog.training.duration}</Typography>
                                </Box>
                            </Box>
                            <TextField fullWidth type="date" label="Изберете датум" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
                            <TextField fullWidth type="time" label="Изберете време" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
                            <TextField fullWidth multiline rows={3} label="Дополнителни забелешки (опционално)" placeholder="Напишете ги вашите цели или специјални потреби..." />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
                    <Button onClick={() => setBookingDialog({ open: false, training: null })} variant="outlined" sx={{ px: 4 }}>Откажи</Button>
                    <Button onClick={handleBookingConfirm} variant="contained" startIcon={<ShoppingCart />}
                            sx={{ px: 4, backgroundColor: '#AB47BC', '&:hover': { backgroundColor: '#9C27B0' } }}>
                        Купи сега
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BuyGymExercise;